import React from 'react';
import { action, observable, computed, makeObservable, reaction } from 'mobx';
import { ApiAccountDefinition, ApiAccount } from '@api';
import { FormInstance } from '@ui-library/form';
import { DestinationAccount } from '@stores/destination/types';

export class AccountManagementStore {
  private reactionDisposer: (() => void) | null = null;

  public form: FormInstance;

  public supportedAccountTypes: string[] = [];

  public resourceType: 'destination' | 'source';

  public resourceDefinitionName: string;

  @observable private _legacyAccounts: DestinationAccount[] = [];

  @observable private accountId: string;

  @observable private accountDefinitionName: string;

  @observable private accounts: ApiAccount[];

  @observable private accountDefinitions: ApiAccountDefinition[];

  @observable private deleteAccountId: string;

  constructor(
    form: FormInstance,
    config: {
      resourceType: 'destination' | 'source';
      resourceDefinitionName: string;
      supportedAccountTypes?: string[];
    },
  ) {
    makeObservable(this);

    this.form = form;
    this.accountId = '';
    this.accountDefinitionName = '';
    this.accountDefinitions = [];
    this.accounts = [];
    this.deleteAccountId = '';
    this.supportedAccountTypes = config.supportedAccountTypes || [];
    this.resourceType = config.resourceType;
    this.resourceDefinitionName = config.resourceDefinitionName;
  }

  @action.bound
  public setLegacyAccounts(accounts: DestinationAccount[]) {
    this._legacyAccounts = accounts;
  }

  @computed
  public get legacyAccounts() {
    return this._legacyAccounts;
  }

  @action.bound
  public setForm(form: FormInstance) {
    this.form = form;
  }

  @action.bound
  public setReactionDisposer(onComplete: (accountId: string) => void) {
    this.reactionDisposer = reaction(
      () => this.accountId,
      (accountId) => {
        if (accountId) {
          onComplete(accountId);
        }
      },
    );
  }

  @action.bound
  public getOAuthPayload() {
    const formData = this.form.getFieldsValue();

    return {
      name: formData.name,
      options: formData.options || {},
      accountDefinitionName: this.accountDefinitionName,
    };
  }

  @action.bound
  public setAccounts(accounts: ApiAccount[]) {
    this.accounts = accounts;
  }

  @computed
  public get compatibleAccounts() {
    return this.accounts;
  }

  public get compatibleAccountDefinitions() {
    return this.accountDefinitions.filter((accountDefinition) =>
      this.supportedAccountTypes.includes(accountDefinition.name),
    );
  }

  @action.bound
  public findAccountById(accountId: string) {
    return this.accounts.find((account) => account.id === accountId);
  }

  @action.bound
  public setAccountId(value: string) {
    this.accountId = value;
  }

  @computed
  public get selectedAccountId(): string {
    return this.accountId;
  }

  @action.bound
  public setAccountDefinitionName(value: string) {
    this.accountDefinitionName = value;
  }

  @computed
  public get selectedAccountDefinition() {
    return this.accountDefinitions.find(
      (accountDef) => accountDef.name === this.accountDefinitionName,
    );
  }

  @action.bound
  public setAccountDefinitions(accountDefinitions: ApiAccountDefinition[]) {
    this.accountDefinitions = accountDefinitions;
  }

  @action.bound
  public isAccountOAuth(accountId: string) {
    const account = this.findAccountById(accountId);

    if (account) {
      const accountDefinition = this.accountDefinitions.find(
        (accountDef) => accountDef.name === account.accountDefinitionName,
      );

      return accountDefinition?.uiConfig.isOAuth || false;
    }

    return false;
  }

  @action.bound
  public setDeleteAccountId(value: string) {
    this.deleteAccountId = value;
  }

  @computed
  public get selectedDeleteAccountId() {
    return this.deleteAccountId;
  }

  // Cleanup method
  public dispose() {
    if (this.reactionDisposer) {
      this.reactionDisposer();
      this.reactionDisposer = null;
    }
  }

  public reset() {
    this.accountId = '';
    this.accountDefinitionName = '';
    this.form.resetFields();
  }
}

export const AccountManagementCtx = React.createContext<AccountManagementStore | undefined>(
  undefined,
);

export const useAccountManagementStore = () => {
  const context = React.useContext(AccountManagementCtx);

  if (!context) {
    throw new Error('useAccountManagementStore must be used within AccountManagementCtx.Provider');
  }
  return context;
};
